import { useEffect, useState, useCallback, useMemo } from "react";
import dataService from "../../../../../helpers/dataService";
import useStore from "../../../../../context/useStore";
import { toast } from "react-toastify";

const useEpSessionV2 = (epId) => {
  const [state] = useStore();
  const [ep, setEp] = useState(null);
  const [inscriptions, setInscriptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingInscriptions, setIsLoadingInscriptions] = useState(true);
  const [errors, setErrors] = useState({});

  const [jury, setJury] = useState([]);
  const [isLoadingJury, setIsLoadingJury] = useState(true);

  const [writerPresident, setWriterPresident] = useState({
    writer: "",
    president: "",
  });
  const [isSavingWriters, setIsSavingWriters] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isSavingCalling, setIsSavingCalling] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const isDirector =
    state.auth.user.role === "CAF_DIRECTOR" || state.auth.user.isDirector;

  const fetchEp = useCallback(() => {
    setIsLoading(true);
    dataService.get(
      `eps/${epId}`,
      (data) => {
        setEp(data);
        setWriterPresident({
          writer: data.writer || "",
          president: data.president || "",
        });
        setIsLoading(false);
      },
      (err) => {
        setErrors(err);
        setIsLoading(false);
      },
    );
  }, [epId]);

  const fetchInscriptions = useCallback(() => {
    setIsLoadingInscriptions(true);
    dataService.get(
      `eps/${epId}/lister-inscriptions`,
      (data) => {
        setInscriptions(Array.isArray(data) ? data : []);
        setIsLoadingInscriptions(false);
      },
      (err) => {
        setErrors(err);
        setIsLoadingInscriptions(false);
      },
    );
  }, [epId]);

  const fetchJury = useCallback(() => {
    if (state.auth.user.role === "AGENT_PARTNER") {
      setIsLoadingJury(false);
      return;
    }
    setIsLoadingJury(true);
    dataService.get(
      `eps/${epId}/jury`,
      (data) => {
        setJury(Array.isArray(data) ? data : []);
        setIsLoadingJury(false);
      },
      (err) => {
        setErrors(err);
        setIsLoadingJury(false);
      },
    );
  }, [epId, state.auth.user.role]);

  useEffect(() => {
    if (epId) {
      fetchEp();
      fetchInscriptions();
      fetchJury();
    }
  }, [epId, fetchEp, fetchInscriptions, fetchJury]);

  const startEp = () => {
    setIsStarting(true);
    dataService.patch(
      `eps/${epId}/demarrer`,
      {},
      () => {
        fetchEp();
        setIsStarting(false);
      },
      (err) => {
        setErrors(err);
        setIsStarting(false);
      },
    );
  };

  const callMember = (userManagerId, isPresent) => {
    setJury((prev) =>
      prev.map((j) =>
        j.userManager.id === userManagerId ? { ...j, isPresent } : j,
      ),
    );
  };

  const saveCalling = () => {
    setIsSavingCalling(true);
    const presentIds = jury
      .filter((j) => j.isPresent)
      .map((j) => j.userManager.id);

    dataService.patch(
      `eps/${epId}/faire-appel-jury`,
      presentIds,
      () => {
        fetchEp();
        setIsSavingCalling(false);
      },
      (err) => {
        setErrors(err);
        setIsSavingCalling(false);
      },
    );
  };

  const saveWriterPresident = () => {
    setIsSavingWriters(true);
    dataService.patch(
      `eps/${epId}/definir-roles-jury`,
      writerPresident,
      () => {
        toast.success("Rédacteur et président mis à jour");
        fetchEp();
        setIsSavingWriters(false);
      },
      (err) => {
        setErrors(err);
        setIsSavingWriters(false);
      },
    );
  };

  const closeSession = () => {
    setIsClosing(true);
    dataService.patch(
      `eps/${epId}/cloturer`,
      {},
      () => {
        toast.success("Session clôturée par le président");
        fetchEp();
        setIsClosing(false);
      },
      (err) => {
        setErrors(err);
        setIsClosing(false);
      },
    );
  };

  const presentJuryMembers = jury.filter((j) => j.isPresent);

  const stats = useMemo(
    () =>
      inscriptions.reduce(
        (acc, i) => {
          acc.total++;
          if (i.decisionId) acc.decisionsTaken++;
          if (i.type === "SUSPENSION") acc.suspensions++;
          if (i.type === "SUPPRESSION") acc.suppressions++;
          return acc;
        },
        { total: 0, decisionsTaken: 0, suspensions: 0, suppressions: 0 },
      ),
    [inscriptions],
  );

  return {
    ep,
    setEp,
    inscriptions,
    setInscriptions,
    jury,
    isLoading,
    isLoadingInscriptions,
    isLoadingJury,
    errors,
    isDirector,
    startEp,
    isStarting,
    callMember,
    saveCalling,
    isSavingCalling,
    writerPresident,
    setWriterPresident,
    saveWriterPresident,
    isSavingWriters,
    closeSession,
    isClosing,
    presentJuryMembers,
    stats,
    fetchInscriptions,
    fetchEp,
  };
};

export default useEpSessionV2;
